import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";

const MIN_MENU_WIDTH = 170;
const SCREEN_MARGIN = 16;
const GAP = 6;

// Row action menu ("Edit / View / Remove"). The menu is position: fixed so a table never clips it,
// it is sized to its content, kept fully inside the viewport (it flips above the button when there
// is no room below) and it closes when an option is clicked or the page scrolls/resizes.
export const TableDropdown = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({ visibility: 'hidden' });
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!wrapperRef.current || !menuRef.current) return;

    const button = wrapperRef.current.querySelector('button') || wrapperRef.current;
    const toggle = button.getBoundingClientRect();
    const width = menuRef.current.offsetWidth;
    const height = menuRef.current.offsetHeight;

    // Align the menu's right edge with the toggle, then keep it inside the viewport
    const left = Math.min(
      Math.max(SCREEN_MARGIN, toggle.right - width),
      window.innerWidth - width - SCREEN_MARGIN
    );

    let top = toggle.bottom + GAP;
    if (top + height > window.innerHeight - SCREEN_MARGIN) {
      top = Math.max(SCREEN_MARGIN, toggle.top - height - GAP); // open upwards
    }

    setMenuStyle({ top, left, visibility: 'visible' });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Measure the real menu size before it is painted
  useLayoutEffect(() => {
    if (isOpen) updatePosition();
    else setMenuStyle({ visibility: 'hidden' });
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClose = () => setIsOpen(false);
    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('resize', handleClose);
    return () => {
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('resize', handleClose);
    };
  }, [isOpen]);

  return (
    <div className={`tableDropdown custom-table-dropdown ${isOpen ? 'show' : ''}`} ref={wrapperRef}>
      <button
        type="button"
        className="notButton classicToggle"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <FontAwesomeIcon icon={faEllipsisV} />
      </button>
      {isOpen && (
        <div
          ref={menuRef}
          className="tableDropdownMenu show"
          style={{
            position: 'fixed',
            right: 'auto', // the stylesheet sets right: 0, which would stretch the menu to the screen edge
            width: 'max-content',
            minWidth: MIN_MENU_WIDTH,
            maxWidth: `calc(100vw - ${SCREEN_MARGIN * 2}px)`,
            zIndex: 10002,
            ...menuStyle,
          }}
          onClick={() => setIsOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default TableDropdown;
